import { useSyncExternalStore } from 'react';

// What the app says about its offline copy, in one place that React can
// subscribe to: the banner shows the queue and the last sync, the Offline
// page shows the same numbers, and `useInvalidatingMutation` reads the
// queued count to know whether the write it just made went over the wire or
// into the queue.
//
// This is a snapshot of counts, not the queue itself. The rows live in
// IndexedDB (store.ts); reading them is asynchronous, so anything that
// changes them republishes the counts here afterwards.

export interface OfflineSnapshot {
  /** Writes waiting to be sent. */
  readonly queued: number;
  /** Writes the server refused for a reason a retry will not fix. */
  readonly failed: number;
  /** Writes whose row was changed elsewhere while they waited. */
  readonly conflicts: number;
  /** When the queue last drained to nothing, or the manifest last ran; null before either. */
  readonly syncedAt: string | null;
  /** Whether a read on this page was answered from the kept copy rather than the server. */
  readonly servingOffline: boolean;
  /** How many writes have been queued in this session; the toast reads it. */
  readonly queuedEver: number;
}

const EMPTY: OfflineSnapshot = {
  queued: 0,
  failed: 0,
  conflicts: 0,
  syncedAt: null,
  servingOffline: false,
  queuedEver: 0,
};

type Listener = () => void;

const listeners = new Set<Listener>();
let snapshot: OfflineSnapshot = EMPTY;

function publish(next: OfflineSnapshot): void {
  snapshot = next;
  for (const listener of listeners) {
    listener();
  }
}

/**
 * Replace part of what the app says about the copy. A change that changes
 * nothing publishes nothing: every successful read reports that it came from
 * the server, and re-rendering the banner on each of those would be work for
 * no one's benefit.
 */
export function setOfflineState(change: Partial<OfflineSnapshot>): void {
  const next = { ...snapshot, ...change };
  const moved = (Object.keys(next) as (keyof OfflineSnapshot)[]).some(
    (key) => next[key] !== snapshot[key],
  );
  if (moved) {
    publish(next);
  }
}

/** Count one more write into the queue, so the toast can say it was saved offline. */
export function countQueued(): void {
  publish({ ...snapshot, queued: snapshot.queued + 1, queuedEver: snapshot.queuedEver + 1 });
}

/** Forget everything: a sign-out, or a different account signing in. */
export function resetOfflineState(): void {
  publish(EMPTY);
}

/** The current snapshot, for code outside React. */
export function offlineState(): OfflineSnapshot {
  return snapshot;
}

function subscribe(listener: Listener): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

/** What the app says about its offline copy, as a component sees it. */
export function useOfflineState(): OfflineSnapshot {
  return useSyncExternalStore(subscribe, offlineState, offlineState);
}
